import { LitElement } from 'lit';

class ApexValidationRule {
    // eslint-disable-next-line no-useless-constructor
    constructor(errorMessage) {
        this.errorMessage = errorMessage;
    }
}

class ApexValidationRuleRequired extends ApexValidationRule {
    validate(value) {
        if (!value) {
            return false;
        }
        return value.toString().trim().length > 0;
    }
}

var ApexValidationRuleTypes;
(function (ApexValidationRuleTypes) {
    ApexValidationRuleTypes["REQUIRED"] = "required";
    ApexValidationRuleTypes["ALL_REQUIRED"] = "allRequired";
    ApexValidationRuleTypes["MIN"] = "min";
    ApexValidationRuleTypes["MAX"] = "max";
    ApexValidationRuleTypes["REGEX"] = "regex";
    ApexValidationRuleTypes["VALID_VALUES"] = "validValues";
    ApexValidationRuleTypes["VALID_VALUES_SELECTED"] = "validValuesSelected";
    ApexValidationRuleTypes["INVALID_VALUES"] = "invalidValues";
    ApexValidationRuleTypes["NUMERIC_MIN"] = "numericMin";
    ApexValidationRuleTypes["NUMERIC_MAX"] = "numericMax";
    ApexValidationRuleTypes["INCREMENT"] = "increment";
    ApexValidationRuleTypes["NO_PO_BOX"] = "noPoBox";
})(ApexValidationRuleTypes || (ApexValidationRuleTypes = {}));
var ApexValidationRuleDefaults;
(function (ApexValidationRuleDefaults) {
    ApexValidationRuleDefaults[ApexValidationRuleDefaults["MIN_LENGTH"] = 0] = "MIN_LENGTH";
    ApexValidationRuleDefaults[ApexValidationRuleDefaults["MAX_LENGTH"] = 100] = "MAX_LENGTH";
    ApexValidationRuleDefaults["REGEX_RULE"] = ".*";
    ApexValidationRuleDefaults["REGEX_MODIFIERS"] = "ig";
    ApexValidationRuleDefaults[ApexValidationRuleDefaults["NUMERIC_MIN"] = 0] = "NUMERIC_MIN";
    ApexValidationRuleDefaults[ApexValidationRuleDefaults["INCREMENT"] = 5] = "INCREMENT";
})(ApexValidationRuleDefaults || (ApexValidationRuleDefaults = {}));

class ApexValidationRuleMinLength extends ApexValidationRule {
    constructor(errorMessage, minLength = ApexValidationRuleDefaults.MIN_LENGTH) {
        super(errorMessage);
        this.minLength = minLength;
    }
    validate(value) {
        if (!value) {
            // we only want this to be true for no value if the minimum length is zero
            return this.minLength === 0;
        }
        return value.toString().trim().length >= this.minLength;
    }
}

class ApexValidationRuleMaxLength extends ApexValidationRule {
    constructor(errorMessage, maxLength = ApexValidationRuleDefaults.MAX_LENGTH) {
        super(errorMessage);
        this.maxLength = maxLength;
    }
    validate(value) {
        if (!value) {
            return true;
        }
        return value.toString().trim().length <= this.maxLength;
    }
}

class ApexValidationRuleRegex extends ApexValidationRule {
    constructor(errorMessage, regex = ApexValidationRuleDefaults.REGEX_RULE, regexFlags = ApexValidationRuleDefaults.REGEX_MODIFIERS) {
        super(errorMessage);
        this.regex = regex;
        this.regexFlags = regexFlags;
    }
    validate(value) {
        if (!value) {
            return true;
        }
        const validator = new RegExp(this.regex, this.regexFlags);
        return validator.test(value.toString().trim());
    }
}

class ApexValidationRuleValidList extends ApexValidationRule {
    constructor(errorMessage, comparisonList) {
        super(errorMessage);
        this.comparisonList = comparisonList;
    }
    validate(value) {
        if (!value) {
            return true;
        }
        return this.comparisonList.includes(value.toString());
    }
}

class ApexValidationRuleValidListSelected extends ApexValidationRuleValidList {
    validate(value) {
        if (!value) {
            return false;
        }
        return super.validate(value);
    }
}

class ApexValidationRuleInvalidList extends ApexValidationRuleValidList {
    validate(value) {
        if (!value) {
            return true;
        }
        return !super.validate(value);
    }
}

class ApexValidationRuleNumericMin extends ApexValidationRule {
    constructor(errorMessage, numericMin = ApexValidationRuleDefaults.NUMERIC_MIN) {
        super(errorMessage);
        this.numericMin = numericMin;
    }
    validate(value) {
        // if the value is not a number, we cannot validate it so it is false
        if (Number.isNaN(Number(value))) {
            return false;
        }
        return value >= this.numericMin;
    }
}

class ApexValidationRuleNumericMax extends ApexValidationRule {
    constructor(errorMessage, numericMax) {
        super(errorMessage);
        this.numericMax = numericMax;
    }
    validate(value) {
        // if the value is not a number, we cannot validate it so it is false
        if (Number.isNaN(Number(value))) {
            return false;
        }
        return value <= this.numericMax;
    }
}

class ApexValidationRuleIncrement extends ApexValidationRule {
    constructor(errorMessage, increment = ApexValidationRuleDefaults.INCREMENT) {
        super(errorMessage);
        this.increment = increment;
    }
    validate(value) {
        if (Number.isNaN(Number(value))) {
            return false;
        }
        return Number(value) % this.increment === 0;
    }
}

class ApexValidationRuleAllRequired extends ApexValidationRule {
    constructor(errorMessage, totalOptions) {
        super(errorMessage);
        this.errorMessage = errorMessage;
        this.totalOptions = totalOptions;
    }
    validate(value) {
        return this.totalOptions === value.length;
    }
}

class ApexValidationRuleAddressNoPOBox extends ApexValidationRuleRegex {
    constructor(errorMessage) {
        super(errorMessage, 
        /**
         * This regex is a refactored version of the "standard" one that was used on service APIs
         * when the Apex for Web address component was created:
         * /\bPOB\b|\bP\.O\.B\b|\bP\.O\.B\.\b|\bPOST OFFICE BOX\b|\bPO BOX\b|\bPOBOX\b|\bP O BOX\b|\bP\.O\.BOX\b|\bP\.O\. BOX\b|\bP\.O\. BOX\b|\bP\. O\. BOX\b|\bP\. O\. BOX\b|\bCARE OF\b|\bLIEU OF\b|\bESCAPE\b|\bC\/O\b|\bC\/ O\b|\bC \/ O\b|\bC \/O\b|\bPMB\b|\bP\.M\.B\.\b|\bP\.M\.B\b|\bPRIVATE MAIl BOX/gi
         */
        '\\b((P(\\.?|OST|RIVATE)\\s*)(O(\\.?|FFICE)\\s*)?(M(\\.?|AIL)\\s*)?(B(\\.?|OX)?)(\\S*)|((C(ARE)?|LIEU)\\s*\\/?\\s*OF?)|ESCAPE)\\b', 'gi');
    }
    validate(value) {
        // when there is no value, we return true without testing
        if (!value) {
            return true;
        }
        return !super.validate(value);
    }
}

class ApexValidatableBaseComponent extends LitElement {
    constructor() {
        super(...arguments);
        //#region PROPERTIES
        this.validationRulesList = {};
        /**
         * Holds whether or not the component is valid. It defaults to true under the assumption
         * that the component is valid upon load. Some applications like X-Flow will silently trigger
         * validation but not show error messages upon load in which case this value will be updated.
         */
        this._valid = true;
        /**
         * The stored error message value to display in the component's error area.
         */
        this.errorMessage = '';
        /**
         * Automatically set to true when a requireable validation rule has been added.
         */
        this.isRequired = false;
        //#endregion
    }
    //#endregion
    //#region METHODS
    /**
     * If the rule is passed in, then the given rule is updated in the list.
     * If the rule is not passed in, then an existing rule with that name is removed from the list
     *
     * @param ruleName - string
     * @param rule - ApexValidationRule
     * @param collection - string
     */
    updateRule(ruleName, rule, collection = 'validations') {
        if (!this.validationRulesList[collection]) {
            this.validationRulesList[collection] = {};
        }
        if (rule) {
            this.validationRulesList[collection][ruleName] = rule;
        }
        else {
            delete this.validationRulesList[collection][ruleName];
        }
    }
    /**
     * Pass in an ApexValidationRule object to update it in the collection (or remove
     * it if it is a value type that should not be applied in some circumstances).
     * The method can be overridden in the consuming class which should in turn call this one as well.
     *
     * @param rule - string
     * @param collection - string
     */
    updateValidationRule(rule, collection = 'validations') {
        const rules = this._validationRules[collection];
        const text = this._validationText[collection];
        let ruleConfig;
        switch (rule) {
            case ApexValidationRuleTypes.MIN:
                ruleConfig = new ApexValidationRuleMinLength(text[rule], rules[rule]);
                break;
            case ApexValidationRuleTypes.MAX:
                ruleConfig = new ApexValidationRuleMaxLength(text[rule], rules[rule]);
                break;
            case ApexValidationRuleTypes.REQUIRED:
                // if required is false, we want to remove the validation rule so we'll keep the config undefined
                this.isRequired = false;
                if (rules[rule]) {
                    ruleConfig = new ApexValidationRuleRequired(text[rule]);
                    this.isRequired = true;
                }
                break;
            case ApexValidationRuleTypes.REGEX:
                ruleConfig = new ApexValidationRuleRegex(text[rule], rules[rule]);
                break;
            case ApexValidationRuleTypes.VALID_VALUES:
                ruleConfig = new ApexValidationRuleValidList(text[rule], rules[rule]);
                break;
            case ApexValidationRuleTypes.VALID_VALUES_SELECTED:
                ruleConfig = new ApexValidationRuleValidListSelected(text[rule], rules[rule]);
                // we want to make sure that a value is selected so the field is required to have a value and be validated
                this.isRequired = true;
                break;
            case ApexValidationRuleTypes.INVALID_VALUES:
                ruleConfig = new ApexValidationRuleInvalidList(text[rule], rules[rule]);
                break;
            case ApexValidationRuleTypes.NUMERIC_MIN:
                if (rules[rule]) {
                    ruleConfig = new ApexValidationRuleNumericMin(text[rule], rules[rule]);
                }
                break;
            case ApexValidationRuleTypes.NUMERIC_MAX:
                if (rules[rule]) {
                    ruleConfig = new ApexValidationRuleNumericMax(text[rule], rules[rule]);
                }
                break;
            case ApexValidationRuleTypes.INCREMENT:
                if (rules[rule]) {
                    ruleConfig = new ApexValidationRuleIncrement(text[rule], rules[rule]);
                }
                break;
            case ApexValidationRuleTypes.NO_PO_BOX:
                if (rules[rule]) {
                    ruleConfig = new ApexValidationRuleAddressNoPOBox(text[rule]);
                }
                break;
        }
        // @ts-ignore
        this.updateRule(rule, ruleConfig, collection);
    }
    /**
     * The validation checks will run loop through all of the rules that have been added
     * to the validations rules list. If multiple errors are found, they will be marked in
     * the collection as failing but only the error message for the first failing validation
     * will be shown.
     *
     * @param suppressErrors boolean
     * @param collection string
     */
    validate(suppressErrors = false, collection = 'validations', suppressValidityChangedDispatch = false) {
        // we want to capture the current validity state so we know whether or not to dispatch the event after checking validity again
        const previousValidity = this.valid;
        this.valid = true;
        // we only want to attempt to run the rules if the collection exists
        // eslint-disable-next-line no-prototype-builtins
        if (this.validationRulesList.hasOwnProperty(collection)) {
            // we only want to run non-required validation rules for a field that is not required if it has a value
            if (!this.isRequired && !this.validatableValue) {
                this.valid = true;
            }
            else {
                // we clear out a previous error message in case that validation is fixed and another now fails
                this.errorMessage = '';
                /**
                 * By using Array.prototype.some(), we stop iterating when a validation rule fails. Since .some() stops
                 * when the test inside returns true, we return the inverse of the validation result. If all of the validations
                 * pass, .some() itself will return false so we take the inverse of that result to reflect validity of the component.
                 */
                this.valid = !Object.keys(this.validationRulesList[collection]).some((rule) => {
                    // track the rule being run for validatableValue to operate on
                    this.currentValidation = `${collection}_${rule}`;
                    const isInvalid = !this.validationRulesList[collection][rule].validate(this.validatableValue);
                    if (isInvalid) {
                        this.errorMessage = this.validationRulesList[collection][rule].errorMessage;
                    }
                    return isInvalid;
                });
                // unset the validation being tracked since we're past that step
                this.currentValidation = undefined;
                // if the validation state has changed, dispatch the validity-changed event
                if (previousValidity !== this.valid && !suppressValidityChangedDispatch) {
                    this.dispatchEvent(this.generateValidityChangedEvent());
                }
            }
        }
        this.requestUpdate();
        if (!suppressErrors) {
            // @ts-ignore - will be implemented by mixed class
            this.updateErrorMessageDisplay();
        }
    }
    /**
     * Generates a validity changed event with the default structure applicable to most subclasses.
     * Can be overridden by a subclass when additional data needs to be included
     *
     * @protected
     * @returns {CustomEvent<{ [key: string]: any }>}
     * @memberof ApexValidatableBaseComponent
     */
    generateValidityChangedEvent() {
        return new CustomEvent('validity-changed', {
            bubbles: true,
            composed: true,
            detail: {
                value: this.value,
                validatableValue: this.validatableValue,
                valid: this.valid
            }
        });
    }
}

export { ApexValidatableBaseComponent, ApexValidationRule, ApexValidationRuleAddressNoPOBox, ApexValidationRuleAllRequired, ApexValidationRuleDefaults, ApexValidationRuleIncrement, ApexValidationRuleInvalidList, ApexValidationRuleMaxLength, ApexValidationRuleMinLength, ApexValidationRuleNumericMax, ApexValidationRuleNumericMin, ApexValidationRuleRegex, ApexValidationRuleRequired, ApexValidationRuleTypes, ApexValidationRuleValidList, ApexValidationRuleValidListSelected };
//# sourceMappingURL=utils.js.map
